import { ResponsiveSelect } from "@components/responsive-select";
import { getPackPreview, PackType, usePacks } from "@features/images/use-packs";
import { Flex } from "@radix-ui/themes";
import _ from "lodash";
import { useTranslation } from "react-i18next";

export const PackCategories: any = {
  professional_work: {
    content: [
      "professional_natural",
      "professional_black",
      "professional_color",
      "graduation-studio-jumpsuit-shot",
      "neutral",
      "professional_in_front_of_car",
      "professional_in_big_city",
      "professional_with_laptop",
      "keynote_speaker",
      "tech_startup_ceo",
      "professional_on_phone",
      "professional_outdoor_cafe",
      "professional_with_team",
      "professional_at_conference",
      "professional_with_whiteboard",
      "professional_doctor",
      "professional_real_estate",
      "professional_in_luxury_office",
      "professional_at_airport",
      "urban_cyclist",
      "graduation",
      "tinder_profile",
      "studio-elegance-grad-headshot",
      "campus-lawn-grad-portrait",
      "beige-studio-rose-gown-portrait",
      "stepside-candid-grad-portrait",
      "studio-chic-in-white-blazer",
      "poised-grace-in-black-halter-dress",
      "editorial-power-in-black-blazer-dress",
      "modern-boss-look-on-couch",
      "sharp-minimalist-in-black-turtleneck",
    ],
  },
  social_lifestyle: {
    content: [
      "halloween",
      "christmas",
      "easter",
      "valentines_day",
      "autumn_vibes",
      "tinder_profile",
      "red_carpet_photo",
      "instagram",
      "nightlife",
      "travel",
      "lazy_sunday",
      "pride_celebration",
      "wine_tasting",
      "nature",
      "beach_day",
      "fitness",
      "spiritual",
      "book_lover",
    ],
  },
  luxury_glamour: {
    content: [
      "red_carpet_photo",
      "ai_fashion_week",
      "professional_in_luxury_office",
      "michelin_restaurant",
      "private_jet",
      "glamour",
      "polaroid",
      "swimsuit",
    ],
  },
  nightlife_adventure: {
    content: [
      "chernobyl_tour",
      "nightlife",
      "travel",
      "pride_celebration",
      "nature",
      "beach_day",
      "swimsuit",
    ],
  },
  fitness_health: {
    content: [
      "fitness",
      "urban_cyclist",
      "puffer_jacket",
      "nature",
      "beach_day",
      "swimsuit",
      "blossom-tree-graduation-glow",
    ],
  },
  creative_fantasy: {
    content: [
      "royal_portrait",
      "life_on_mars",
      "fantasy_realm",
      "space_explorer",
      "medieval_warrior",
      "apocalypse_survivor",
      "samurai_spirit",
      "robin_hood_style",
      "ancient_greek",
      "mobster",
      "cyberpunk",
      "cosplay",
      "sunset-silhouette-in-white-mesh",
      "tropical-glow-in-floral-white-dress",
      "ocean-sunset-radiance-in-white-bikini",
      "golden-recline-in-white-dress",
      "satin-elegance-at-golden-shore",
      "twilight-beach-confidence-in-olive-dress",
    ],
  },
  vintage_retro: {
    content: [
      "retro_1950s_diner",
      "vintage_1920s_glam",
      "tradwife_aesthetic",
      "industrial_worker",
      "old_money_photo",
      "mob_wife",
      "mobster",
      "y2k_aesthetic",
      "fence-side-farmhouse-vibe",
      "wooden-deck-sitting-pose",
      "black-romper-wheat-field",
    ],
  },
  romantic_intimate: {
    content: [
      "sexy_halloween",
      "sexy_valentine",
      "sexy_easter",
      "boudoir",
      "bridal_beauty",
      "swimsuit",
      "valentines_day",
      "glamour",
      "sunset-bouquet-western-portrait",
      "golden-hour-field-lookback",
      "relaxed-blanket-field-pose",
      "beachwalk-graduation-glow",
      "wildflower-grad-look",
      "white-blossom-spring-look",
      "blossom-tree-graduation-glow",
      "sunflower-garden-glow-portrait",
      "meadow-path-sunset-portrait",
      "backlit-lake-pose-in-beige-bikini",
      "minimalist-sunset-in-black-dress",
    ],
  },
};

export const PackSelect = ({
  value: pack,
  onChange: setModel,
}: {
  value: string | "none";
  onChange: (value: string | "none", pack: PackType | undefined) => void;
}) => {
  const { packs: _packs } = usePacks();
  const packs = _packs.data || [];
  const { t } = useTranslation();

  // Create items array for ResponsiveSelect
  const selectItems = [
    { value: "none", label: t("general.none") },
    ..._.flatMap(
      _.sortBy(Object.keys(PackCategories), (category) =>
        t(["packs_categories." + category + ".name", category])
      ),
      (category) => {
        const categoryPacks = _.sortBy(
          packs.filter((a) => PackCategories[category].content.includes(a.id)),
          (pack) => t(["packs." + pack.id + ".name", pack.id])
        );

        return categoryPacks.map((pack) => ({
          value: pack.id,
          label: (
            <Flex as="span" align="center" gap="2">
              <div
                style={{
                  backgroundImage: `url(${getPackPreview(pack.id)})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  width: "1.5rem",
                  height: "1.5rem",
                }}
                className="w-6 h-6 rounded-full"
              />
              {t(["packs." + pack.id + ".name", pack.id])}
            </Flex>
          ),
          group: t(["packs_categories." + category + ".name", category]),
        }));
      }
    ),
  ];

  return (
    <div className="flex flex-row space-x-2">
      <ResponsiveSelect
        value={pack}
        onChange={(e) =>
          setModel(
            e,
            packs.find((a) => a.id === e)
          )
        }
        size="3"
        className="grow md:max-w-[256px] mb-8"
        items={selectItems}
        renderTrigger={(item) => (
          <Flex as="span" align="center" gap="2">
            {packs.find((a) => a.id === pack) && (
              <div
                style={{
                  backgroundImage: `url(${getPackPreview(pack)})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  width: "1.5rem",
                  height: "1.5rem",
                }}
                className="w-6 h-6 rounded-full shrink-0"
              />
            )}
            {pack === "none"
              ? t("general.none")
              : t(["packs." + pack + ".name", pack, "Inconnu"])}
          </Flex>
        )}
      />
    </div>
  );
};
